/**
 * Step-1 validation for PremiumAgreement variants.
 * Keeps Premium-specific required-field validation separate from Default.
 *
 * @param {string} [suffix] - Appended to every element id/name this class looks up, so two
 * instances can coexist on the same page (e.g. the combined ClimateAndNaturePlus form), each
 * scoped to its own section. Empty string (default) preserves the original single-variant behavior.
 */
export default class ESigningFormPremiumAgreementValidation {
    constructor(suffix = '') {
        this.suffix = suffix;
        this.premiumAgreementExistsState = {
            exists: false,
            pending: false,
            message: '',
        };
    }

    /**
     * @description Root element to scope "required fields" lookups to. Falls back to the whole
     * document when no section wrapper exists (standalone/single-variant forms).
     */
    getScopeRoot() {
        return document.getElementById('premiumAgreementSection' + this.suffix) || document;
    }

    validateStep1() {
        if (!this.hasRequiredFields()) {
            const requiredFieldsMessage =
                eSigningTranslation?.premiumAgreement?.requiredFields ||
                'Du måste fylla i alla obligatoriska fält';
            this.showError(requiredFieldsMessage);
            return false;
        }

        if (!this.validateStep1QuantityField(true)) {
            return false;
        }

        if (!this.validateClimateNatureBiodiversitySelection(true)) {
            return false;
        }

        if (!this.validatePremiumAgreementNotExists(true)) {
            return false;
        }

        return true;
    }

    isStep1Valid() {
        return this.hasRequiredFields()
            && this.validateStep1QuantityField(false)
            && this.validateClimateNatureBiodiversitySelection(false)
            && this.validatePremiumAgreementNotExists(false);
    }

    setPremiumAgreementExistsState(state) {
        this.premiumAgreementExistsState = {
            ...this.premiumAgreementExistsState,
            ...state,
        };

        if (!this.premiumAgreementExistsState.exists || this.premiumAgreementExistsState.pending) {
            this.clearPremiumAgreementExistsError();
        }
    }

    validatePremiumAgreementNotExists(showError) {
        if (this.premiumAgreementExistsState.pending) {
            return false;
        }

        if (!this.premiumAgreementExistsState.exists) {
            this.clearPremiumAgreementExistsError();
            return true;
        }

        if (showError) {
            const message = this.premiumAgreementExistsState.message
                || eSigningTranslation?.premiumAgreement?.agreementAlreadyExists
                || 'Det finns redan ett avtal för den här sorten och skördeåret';
            this.showPremiumAgreementExistsError(message);
        }

        return false;
    }

    showPremiumAgreementExistsError(message) {
        const errorElement = document.getElementById('premiumAgreementExists-error' + this.suffix);
        if (!errorElement) {
            this.showError(message);
            return;
        }

        errorElement.textContent = message;
        errorElement.style.display = 'block';
    }

    clearPremiumAgreementExistsError() {
        const errorElement = document.getElementById('premiumAgreementExists-error' + this.suffix);
        if (!errorElement) {
            return;
        }

        errorElement.textContent = '';
        errorElement.style.display = 'none';
    }

    hasRequiredFields() {
        const requiredFields = this.getRequiredStep1Fields();
        if (!requiredFields.length) {
            return false;
        }

        return requiredFields.every((field) => (field.value || '').trim() !== '');
    }

    getContractQuantityField() {
        return document.getElementById('contractQuantity' + this.suffix)
            || document.getElementById('premiumHectare' + this.suffix);
    }

    getContractQuantityErrorElement() {
        return document.getElementById('contractQuantity-error' + this.suffix)
            || document.getElementById('premiumHectare-error' + this.suffix);
    }

    validateStep1QuantityField(showError) {
        const contractQuantityField = this.getContractQuantityField();
        if (!contractQuantityField) {
            return false;
        }

        const rawValue = (contractQuantityField.value || '').trim();
        if (!rawValue) {
            this.clearPremiumHectareError();
            return false;
        }

        const hectareValue = Number(rawValue);
        const min = Number.parseFloat(contractQuantityField.getAttribute('min'));
        const max = Number.parseFloat(contractQuantityField.getAttribute('max'));
        const isValid = Number.isInteger(hectareValue) && hectareValue >= min && hectareValue <= max;

        if (!isValid && showError) {
            const hectareRangeMessage =
                eSigningTranslation?.premiumAgreement?.hectareRange ||
                `Värdet måste vara mellan ${min} och ${max}`;
            this.showPremiumHectareError(hectareRangeMessage);
        }

        if (isValid) {
            this.clearPremiumHectareError();
        }

        return isValid;
    }

    getClimateNatureBiodiversityCheckboxes() {
        return Array.from(document.querySelectorAll('input[name="climateNatureBiodiversityMeasure' + this.suffix + '"]'));
    }

    getClimateNatureDeferredCheckbox() {
        return document.getElementById('biodiversityMeasureDeferred' + this.suffix);
    }

    showClimateNatureMeasuresError(message) {
        const errorElement = document.getElementById('climateNatureMeasures-error' + this.suffix);
        if (!errorElement) {
            return;
        }

        errorElement.textContent = message;
        errorElement.style.display = 'block';
    }

    clearClimateNatureMeasuresError() {
        const errorElement = document.getElementById('climateNatureMeasures-error' + this.suffix);
        if (!errorElement) {
            return;
        }

        errorElement.textContent = '';
        errorElement.style.display = 'none';
    }

    validateClimateNatureBiodiversitySelection(showError) {
        const regularCheckboxes = this.getClimateNatureBiodiversityCheckboxes();
        const deferredCheckbox = this.getClimateNatureDeferredCheckbox();

        // Section does not exist for other premium variants
        if (!regularCheckboxes.length && !deferredCheckbox) {
            return true;
        }

        const checkedRegularCount = regularCheckboxes.filter((checkbox) => checkbox.checked).length;
        const deferredChecked = !!deferredCheckbox?.checked;
        const hasOnlyDeferred = deferredChecked && checkedRegularCount === 0;
        const hasTwoOrMoreRegular = !deferredChecked && checkedRegularCount >= 2;
        const isValid = hasOnlyDeferred || hasTwoOrMoreRegular;

        if (!isValid && showError) {
            const validationMessage =
                eSigningTranslation?.premiumAgreement?.climateNatureMeasuresValidationMessage
                || 'Kryssa i minst 2 alternativ eller valj Koppliteras i efterhand';
            this.showClimateNatureMeasuresError(validationMessage);
        }

        if (isValid) {
            this.clearClimateNatureMeasuresError();
        }

        return isValid;
    }

    getRequiredStep1Fields() {
        return Array.from(this.getScopeRoot().querySelectorAll('.form-element.step1[data-step1-required="true"]'));
    }

    showError(message) {
        const visibleStep = document.querySelector('.steps:not(.hidden)');
        const errorElement =
            visibleStep?.querySelector('.validate-before-add-error-message') ||
            document.querySelector('.validate-before-add-error-message');

        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';

            setTimeout(() => {
                errorElement.style.display = 'none';
            }, 5000);
        }
    }

    showPremiumHectareError(message) {
        const inputElement = this.getContractQuantityField();
        const errorElement = this.getContractQuantityErrorElement();

        if (!inputElement || !errorElement) {
            return;
        }

        errorElement.textContent = message;
        errorElement.style.display = 'block';
        inputElement.classList.add('input-validation-error');
    }

    clearPremiumHectareError() {
        const inputElement = this.getContractQuantityField();
        const errorElement = this.getContractQuantityErrorElement();

        if (!inputElement || !errorElement) {
            return;
        }

        errorElement.textContent = '';
        errorElement.style.display = 'none';
        inputElement.classList.remove('input-validation-error');
    }
}
